import { FormEvent, useState, useEffect, useCallback } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import {
  Clock,
  Code2,
  Timer,
  Plus,
  Loader2,
  Settings,
  RefreshCw,
  List,
  Trash2,
  Edit3,
  Save,
  X,
  AlertTriangle,
} from 'lucide-react';
import { toast } from 'sonner';
import {
  CodeOperation,
  CreateCodeOperationPayload,
  CreateTimerPayload,
  useBiotransacSettings,
} from '../../hooks/useBiotransacSettings';

/* ─── Types ──────────────────────────────────────────────── */

type SettingsMenu = 'timers' | 'code-operations';

/* ─── Utils ──────────────────────────────────────────────── */

function toNumber(value: string): number {
  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : 0;
}

/* ─── Animation constants ────────────────────────────────── */

const EASE_OUT: [number, number, number, number] = [0.22, 1, 0.36, 1];

const fadeUp = {
  hidden: { opacity: 0, y: 20 },
  visible: (i = 0) => ({
    opacity: 1,
    y: 0,
    transition: { duration: 0.45, ease: EASE_OUT, delay: i * 0.08 },
  }),
};

const panelVariants = {
  hidden: (dir: number) => ({ opacity: 0, x: dir * 20 }),
  visible: {
    opacity: 1,
    x: 0,
    transition: { duration: 0.3, ease: EASE_OUT },
  },
  exit: (dir: number) => ({
    opacity: 0,
    x: dir * -20,
    transition: { duration: 0.2, ease: 'easeIn' as const },
  }),
};

/* ─── Design tokens ──────────────────────────────────────── */

const GREEN = '#016951';
const GREEN_GRAD = 'linear-gradient(to bottom right, #016951, #014a3a)';

/* ─── Shared sub-components ──────────────────────────────── */

function FieldLabel({ htmlFor, children }: { htmlFor?: string; children: React.ReactNode }) {
  return (
    <label
      htmlFor={htmlFor}
      style={{
        display: 'block',
        fontSize: '0.75rem',
        fontWeight: 700,
        color: '#6b7280',
        textTransform: 'uppercase',
        letterSpacing: '0.07em',
        marginBottom: 8,
      }}
    >
      {children}
    </label>
  );
}

function StyledInput({
  id,
  type = 'text',
  placeholder,
  value,
  onChange,
  required,
  min,
  disabled,
  icon,
}: {
  id?: string;
  type?: string;
  placeholder?: string;
  value: string | number;
  onChange: (e: React.ChangeEvent<HTMLInputElement>) => void;
  required?: boolean;
  min?: number;
  disabled?: boolean;
  icon?: React.ReactNode;
}) {
  return (
    <div
      style={{
        display: 'flex',
        alignItems: 'center',
        height: 48,
        background: '#f8fafc',
        border: '1.5px solid #e2e8f0',
        borderRadius: 12,
        padding: '0 14px',
        gap: 10,
        transition: 'border-color 0.2s, box-shadow 0.2s',
      }}
      className="focus-within:!border-blue-500 focus-within:shadow-[0_0_0_3px_rgba(59,130,246,0.15)]"
    >
      {icon && (
        <span style={{ color: '#94a3b8', flexShrink: 0, display: 'flex' }}>{icon}</span>
      )}
      <input
        id={id}
        type={type}
        placeholder={placeholder}
        value={value}
        onChange={onChange}
        required={required}
        min={min}
        disabled={disabled}
        style={{
          flex: 1,
          border: 'none',
          background: 'transparent',
          outline: 'none',
          fontSize: '0.875rem',
          color: '#1e293b',
          height: '100%',
        }}
      />
    </div>
  );
}

function StyledTextarea({
  id,
  placeholder,
  value,
  onChange,
  required,
  rows = 3,
}: {
  id?: string;
  placeholder?: string;
  value: string;
  onChange: (e: React.ChangeEvent<HTMLTextAreaElement>) => void;
  required?: boolean;
  rows?: number;
}) {
  return (
    <textarea
      id={id}
      placeholder={placeholder}
      value={value}
      onChange={onChange}
      required={required}
      rows={rows}
      style={{
        width: '100%',
        padding: '12px 14px',
        background: '#f8fafc',
        border: '1.5px solid #e2e8f0',
        borderRadius: 12,
        fontSize: '0.875rem',
        color: '#1e293b',
        outline: 'none',
        resize: 'none',
        transition: 'border-color 0.2s, box-shadow 0.2s',
        fontFamily: 'inherit',
      }}
      className="focus:!border-blue-500 focus:shadow-[0_0_0_3px_rgba(59,130,246,0.15)]"
    />
  );
}

function ToggleSwitch({
  checked,
  onChange,
  label,
}: {
  checked: boolean;
  onChange: () => void;
  label: string;
}) {
  return (
    <button
      type="button"
      onClick={onChange}
      style={{
        display: 'flex',
        alignItems: 'center',
        gap: 10,
        height: 48,
        padding: '0 14px',
        background: checked ? 'rgba(59,130,246,0.07)' : '#f8fafc',
        border: `1.5px solid ${checked ? '#3b82f6' : '#e2e8f0'}`,
        borderRadius: 12,
        cursor: 'pointer',
        transition: 'all 0.2s',
        width: '100%',
      }}
      aria-pressed={checked}
      aria-label={`Statut: ${label}`}
    >
      {/* Track */}
      <span
        style={{
          position: 'relative',
          width: 36,
          height: 20,
          borderRadius: 10,
          background: checked ? GREEN : '#cbd5e1',
          flexShrink: 0,
          transition: 'background 0.25s',
        }}
      >
        <motion.span
          animate={{ x: checked ? 16 : 2 }}
          transition={{ type: 'spring', stiffness: 500, damping: 30 }}
          style={{
            position: 'absolute',
            top: 2,
            width: 16,
            height: 16,
            borderRadius: '50%',
            background: 'white',
            boxShadow: '0 1px 3px rgba(0,0,0,0.2)',
          }}
        />
      </span>
      <span
        style={{
          fontSize: '0.875rem',
          fontWeight: 600,
          color: checked ? GREEN : '#64748b',
        }}
      >
        {label}
      </span>
    </button>
  );
}

function PrimaryBtn({
  loading,
  label,
  loadingLabel,
}: {
  loading: boolean;
  label: string;
  loadingLabel: string;
}) {
  return (
    <motion.button
      type="submit"
      disabled={loading}
      whileHover={{ scale: loading ? 1 : 1.02 }}
      whileTap={{ scale: 0.97 }}
      style={{
        display: 'flex',
        alignItems: 'center',
        gap: 8,
        padding: '0 20px',
        height: 42,
        background: loading ? '#93c5fd' : GREEN_GRAD,
        border: 'none',
        borderRadius: 12,
        color: 'white',
        fontWeight: 700,
        fontSize: '0.875rem',
        cursor: loading ? 'not-allowed' : 'pointer',
        boxShadow: loading ? 'none' : '0 4px 14px rgba(37,99,235,0.35)',
        transition: 'background 0.2s, box-shadow 0.2s',
      }}
    >
      {loading && <Loader2 style={{ width: 15, height: 15, animation: 'spin .75s linear infinite' }} />}
      {loading ? loadingLabel : label}
    </motion.button>
  );
}

export function SecondaryBtn({
  loading,
  label,
  loadingLabel,
}: {
  loading: boolean;
  label: string;
  loadingLabel: string;
}) {
  return (
    <motion.button
      type="submit"
      disabled={loading}
      whileHover={{ scale: loading ? 1 : 1.02 }}
      whileTap={{ scale: 0.97 }}
      style={{
        display: 'flex',
        alignItems: 'center',
        gap: 8,
        padding: '0 20px',
        height: 42,
        background: 'white',
        border: '1.5px solid #e2e8f0',
        borderRadius: 12,
        color: '#374151',
        fontWeight: 700,
        fontSize: '0.875rem',
        cursor: loading ? 'not-allowed' : 'pointer',
        opacity: loading ? 0.6 : 1,
        transition: 'border-color 0.2s',
      }}
    >
      {loading && <Loader2 style={{ width: 15, height: 15, animation: 'spin .75s linear infinite' }} />}
      {loading ? loadingLabel : label}
    </motion.button>
  );
}

interface SectionCardProps {
  icon: React.ReactNode;
  iconColor?: string;
  title: string;
  subtitle?: string;
  children: React.ReactNode;
  index?: number;
}

function SectionCard({ icon, iconColor = GREEN, title, subtitle, children, index = 0 }: SectionCardProps) {
  return (
    <motion.div
      custom={index}
      variants={fadeUp}
      initial="hidden"
      animate="visible"
      style={{
        background: 'white',
        borderRadius: 20,
        boxShadow: '0 4px 24px rgba(0,0,0,0.07)',
        border: '1px solid rgba(0,0,0,0.06)',
        overflow: 'hidden',
      }}
    >
      {/* Card header */}
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          gap: 12,
          padding: '14px 20px',
          borderBottom: '1px solid #f1f5f9',
          background: '#fafbff',
        }}
      >
        <div
          style={{
            width: 36,
            height: 36,
            borderRadius: 10,
            background: `${iconColor}18`,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            flexShrink: 0,
            color: iconColor,
          }}
        >
          {icon}
        </div>
        <div>
          <p style={{ fontSize: '0.9rem', fontWeight: 700, color: '#1e293b' }}>{title}</p>
          {subtitle && (
            <p style={{ fontSize: '0.75rem', color: '#94a3b8', marginTop: 2 }}>{subtitle}</p>
          )}
        </div>
      </div>

      {/* Card body */}
      <div style={{ padding: 20 }}>{children}</div>
    </motion.div>
  );
}

/* ─── Main component ─────────────────────────────────────── */

export function SettingsPage() {
  const {
    createTimer,
    getAllTimers,
    getTimerByCode,
    patchTimer,
    deleteTimer,
    createCodeOperation,
    getAllCodeOperations,
    patchCodeOperation,
    deleteCodeOperation,
  } = useBiotransacSettings();

  const [activeMenu, setActiveMenu] = useState<SettingsMenu>('timers');
  const [direction, setDirection] = useState(1);

  /* ── Form states ── */
  const [timerCreateForm, setTimerCreateForm] = useState<CreateTimerPayload>({
    code: '',
    libelle: '',
    valeurMinutes: 0,
    actif: true,
  });
  const [timerLookupCode, setTimerLookupCode] = useState('');
  void setTimerLookupCode; // Supprime l'avertissement TS noUnusedLocals — sera utilisé par la recherche par code

  const [codeOpCreateForm, setCodeOpCreateForm] = useState<CreateCodeOperationPayload>({
    codeOpe: '',
    libelle: '',
    description: '',
    statut: '',
  });

  /* ── List states ── */
  const [codeOperationsList, setCodeOperationsList] = useState<CodeOperation[]>([]);
  const [loadingCodeOps, setLoadingCodeOps] = useState(false);
  const [codeOpsLoaded, setCodeOpsLoaded] = useState(false);
  const [editingCodeOpId, setEditingCodeOpId] = useState<number | null>(null);
  const [editingCodeOpData, setEditingCodeOpData] = useState<CodeOperation | null>(null);

  const [timersList, setTimersList] = useState<any[]>([]);
  const [loadingTimers, setLoadingTimers] = useState(false);
  const [timersLoaded, setTimersLoaded] = useState(false);
  const [editingTimerId, setEditingTimerId] = useState<number | null>(null);
  const [editingTimerData, setEditingTimerData] = useState<any>(null);

  /* ── Async state ── */
  const [pendingAction, setPendingAction] = useState<string | null>(null);

  /* ── Dialog state ── */
  const [deleteDialog, setDeleteDialog] = useState<{
    isOpen: boolean;
    type: 'timer' | 'code-operation' | null;
    id: number | null;
    name: string;
  }>({
    isOpen: false,
    type: null,
    id: null,
    name: '',
  });

  /* ── Action runner ── */
  const runAction = async (actionKey: string, label: string, action: () => Promise<unknown>) => {
    setPendingAction(actionKey);
    try {
      await action();
      toast.success('Opération réussie', { description: label });
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Erreur inconnue';
      toast.error('Échec de l\'opération', { description: message });
    } finally {
      setPendingAction(null);
    }
  };

  /* ── Handlers ── */
  const handleCreateTimer = async (e: FormEvent) => {
    e.preventDefault();
    await runAction('create-timer', 'Timer créé avec succès.', () => createTimer(timerCreateForm));
    loadTimers();
  };

  // eslint-disable-next-line @typescript-eslint/no-unused-vars -- fonctionnalité recherche par code (à brancher sur l'UI)
  const handleGetTimerByCode = (e: FormEvent) => {
    e.preventDefault();
    runAction('get-timer-by-code', 'Timer trouvé.', () => getTimerByCode(timerLookupCode));
  };
  void handleGetTimerByCode; // Supprime l'avertissement TS noUnusedLocals

  const loadTimers = useCallback(async () => {
    setLoadingTimers(true);
    try {
      const result = await getAllTimers();
      setTimersList(result);
      setTimersLoaded(true);
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Erreur lors du chargement';
      toast.error('Échec du chargement des timers', { description: message });
    } finally {
      setLoadingTimers(false);
    }
  }, [getAllTimers]);

  const startEditingTimer = (timer: any) => {
    setEditingTimerId(timer.id ?? null);
    setEditingTimerData({ ...timer });
  };

  const cancelEditingTimer = () => {
    setEditingTimerId(null);
    setEditingTimerData(null);
  };

  const updateEditingTimerField = (field: string, value: any) => {
    if (editingTimerData) {
      setEditingTimerData({ ...editingTimerData, [field]: value });
    }
  };

  const handlePatchTimer = async () => {
    if (!editingTimerData || !editingTimerData.id) return;

    setPendingAction(`edit-timer-${editingTimerData.id}`);
    try {
      await patchTimer(editingTimerData.id, {
        code: editingTimerData.code,
        libelle: editingTimerData.libelle,
        valeurMinutes: editingTimerData.valeurMinutes,
        actif: editingTimerData.actif,
      });
      toast.success('Timer mis à jour');
      setEditingTimerId(null);
      setEditingTimerData(null);
      loadTimers();
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Erreur lors de la mise à jour';
      toast.error('Échec de la mise à jour', { description: message });
    } finally {
      setPendingAction(null);
    }
  };

  const openDeleteTimerDialog = (timer: any) => {
    setDeleteDialog({
      isOpen: true,
      type: 'timer',
      id: timer.id,
      name: timer.libelle || timer.code,
    });
  };

  const handleDeleteTimer = async () => {
    if (!deleteDialog.id) return;

    setPendingAction(`delete-timer-${deleteDialog.id}`);
    try {
      await deleteTimer(deleteDialog.id);
      toast.success('Timer supprimé');
      setDeleteDialog({ isOpen: false, type: null, id: null, name: '' });
      loadTimers();
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Erreur lors de la suppression';
      toast.error('Échec de la suppression', { description: message });
    } finally {
      setPendingAction(null);
    }
  };

  const handleCreateCodeOperation = async (e: FormEvent) => {
    e.preventDefault();
    await runAction('create-code-operation', 'Code opération créé.', () =>
      createCodeOperation(codeOpCreateForm)
    );
    // Recharger la liste après création
    loadCodeOperations();
  };

  const handlePatchCodeOperation = async () => {
    if (!editingCodeOpData || !editingCodeOpData.id) return;

    setPendingAction(`edit-${editingCodeOpData.id}`);
    try {
      await patchCodeOperation(editingCodeOpData.id, {
        codeOpe: editingCodeOpData.codeOpe,
        libelle: editingCodeOpData.libelle,
        description: editingCodeOpData.description,
        statut: editingCodeOpData.statut,
      });
      toast.success('Code opération mis à jour');
      setEditingCodeOpId(null);
      setEditingCodeOpData(null);
      loadCodeOperations();
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Erreur lors de la mise à jour';
      toast.error('Échec de la mise à jour', { description: message });
    } finally {
      setPendingAction(null);
    }
  };

  const startEditingCodeOp = (codeOp: CodeOperation) => {
    setEditingCodeOpId(codeOp.id ?? null);
    setEditingCodeOpData({ ...codeOp });
  };

  const cancelEditingCodeOp = () => {
    setEditingCodeOpId(null);
    setEditingCodeOpData(null);
  };

  const updateEditingField = (field: keyof CodeOperation, value: string) => {
    if (editingCodeOpData) {
      setEditingCodeOpData({ ...editingCodeOpData, [field]: value });
    }
  };

  const loadCodeOperations = useCallback(async () => {
    setLoadingCodeOps(true);
    try {
      const result = await getAllCodeOperations();
      setCodeOperationsList(result);
      setCodeOpsLoaded(true);
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Erreur lors du chargement';
      toast.error('Échec du chargement', { description: message });
    } finally {
      setLoadingCodeOps(false);
    }
  }, [getAllCodeOperations]);

  const openDeleteCodeOperationDialog = (codeOp: CodeOperation) => {
    setDeleteDialog({
      isOpen: true,
      type: 'code-operation',
      id: codeOp.id ?? null,
      name: codeOp.libelle || codeOp.codeOpe,
    });
  };

  const handleDeleteCodeOperation = async () => {
    if (!deleteDialog.id) return;

    setPendingAction(`delete-${deleteDialog.id}`);
    try {
      await deleteCodeOperation(deleteDialog.id);
      toast.success('Code opération supprimé');
      setDeleteDialog({ isOpen: false, type: null, id: null, name: '' });
      loadCodeOperations();
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Erreur lors de la suppression';
      toast.error('Échec de la suppression', { description: message });
    } finally {
      setPendingAction(null);
    }
  };

  const closeDeleteDialog = () => {
    setDeleteDialog({ isOpen: false, type: null, id: null, name: '' });
  };

  const switchTab = (tab: SettingsMenu) => {
    if (tab === activeMenu) return;
    setDirection(tab === 'code-operations' ? 1 : -1);
    setActiveMenu(tab);
  };

  // Charger automatiquement les données quand on change d'onglet
  useEffect(() => {
    if (activeMenu === 'timers' && !timersLoaded) {
      loadTimers();
    } else if (activeMenu === 'code-operations' && !codeOpsLoaded) {
      loadCodeOperations();
    }
  }, [activeMenu, timersLoaded, codeOpsLoaded, loadTimers, loadCodeOperations]);

  /* ── Tabs config ── */
  const tabs: { key: SettingsMenu; label: string; icon: React.ReactNode }[] = [
    { key: 'timers', label: 'Configuration du temps', icon: <Clock style={{ width: 15, height: 15 }} /> },
    { key: 'code-operations', label: 'Codes opérations', icon: <Code2 style={{ width: 15, height: 15 }} /> },
  ];

  /* ──────────────────────────────────────────────────────── */

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 24 }}>

      {/* ── Hero header ─────────────────────────────────── */}
      <motion.div
        initial={{ opacity: 0, y: -16 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5, ease: EASE_OUT }}
        style={{
          background: 'linear-gradient(to bottom right, #016951, #014a3a)',
          borderRadius: 20,
          padding: '24px 28px',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          boxShadow: '0 8px 32px rgba(1,105,81,0.28)',
        }}
      >
        <div style={{ display: 'flex', alignItems: 'center', gap: 16 }}>
          <div
            style={{
              width: 48,
              height: 48,
              borderRadius: 14,
              background: 'rgba(255,255,255,0.15)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              backdropFilter: 'blur(4px)',
            }}
          >
            <Settings style={{ width: 22, height: 22, color: 'white' }} />
          </div>
          <div>
            <h2
              style={{
                fontSize: '1.3rem',
                fontWeight: 800,
                color: 'white',
                letterSpacing: '-0.01em',
              }}
            >
              Paramétrage
            </h2>
            <p style={{ fontSize: '0.8rem', color: 'rgba(147,197,253,0.85)', marginTop: 2 }}>
              Configuration des timers de signature et des codes opérations
            </p>
          </div>
        </div>

        <motion.button
          type="button"
          whileHover={{ scale: 1.05 }}
          whileTap={{ scale: 0.95 }}
          onClick={() => {
            toast.info('Page actualisée');
            window.location.reload();
          }}
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: 8,
            padding: '0 16px',
            height: 38,
            background: 'rgba(255,255,255,0.15)',
            border: '1px solid rgba(255,255,255,0.25)',
            borderRadius: 10,
            color: 'white',
            fontSize: '0.82rem',
            fontWeight: 600,
            cursor: 'pointer',
            backdropFilter: 'blur(4px)',
          }}
        >
          <RefreshCw style={{ width: 14, height: 14 }} />
          Actualiser
        </motion.button>
      </motion.div>

      {/* ── Segmented tabs ───────────────────────────────── */}
      <motion.div
        initial={{ opacity: 0, y: 10 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.4, ease: EASE_OUT, delay: 0.1 }}
        style={{
          position: 'relative',
          display: 'inline-flex',
          background: '#f1f5f9',
          borderRadius: 16,
          padding: 4,
          alignSelf: 'flex-start',
        }}
      >
        {/* Sliding indicator */}
        <motion.div
          style={{
            position: 'absolute',
            top: 4,
            bottom: 4,
            background: 'white',
            borderRadius: 12,
            boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
          }}
          animate={{
            left: activeMenu === 'timers' ? 4 : 'calc(50% + 2px)',
            right: activeMenu === 'timers' ? 'calc(50% + 2px)' : 4,
          }}
          transition={{ type: 'spring', stiffness: 420, damping: 34 }}
        />

        {tabs.map((tab) => (
          <button
            key={tab.key}
            type="button"
            onClick={() => switchTab(tab.key)}
            style={{
              position: 'relative',
              zIndex: 1,
              display: 'flex',
              alignItems: 'center',
              gap: 8,
              padding: '10px 20px',
              borderRadius: 12,
              border: 'none',
              background: 'transparent',
              fontWeight: 700,
              fontSize: '0.85rem',
              color: activeMenu === tab.key ? GREEN : '#94a3b8',
              cursor: 'pointer',
              transition: 'color 0.2s',
              whiteSpace: 'nowrap',
            }}
          >
            {tab.icon}
            {tab.label}
          </button>
        ))}
      </motion.div>

      {/* ── Tab content ──────────────────────────────────── */}
      <AnimatePresence mode="wait" custom={direction}>
        {activeMenu === 'timers' ? (
          <motion.div
            key="timers"
            custom={direction}
            variants={panelVariants}
            initial="hidden"
            animate="visible"
            exit="exit"
            style={{
              display: 'grid',
              gridTemplateColumns: 'repeat(auto-fit, minmax(340px, 1fr))',
              gap: 20,
            }}
          >
            {/* ── Créer un timer ── */}
            <SectionCard
              icon={<Timer style={{ width: 18, height: 18 }} />}
              title="Créer un timer de signature"
              subtitle="L'identifiant est auto-incrémenté côté backend."
              index={0}
            >
              <form onSubmit={handleCreateTimer} style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
                <div>
                  <FieldLabel htmlFor="t-code">Code</FieldLabel>
                  <StyledInput
                    id="t-code"
                    placeholder="Ex : TIMER_SIGNATURE"
                    value={timerCreateForm.code}
                    onChange={(e) => setTimerCreateForm((p) => ({ ...p, code: e.target.value }))}
                    required
                  />
                </div>

                <div>
                  <FieldLabel htmlFor="t-libelle">Libellé</FieldLabel>
                  <StyledInput
                    id="t-libelle"
                    placeholder="Ex : Délai signature client"
                    value={timerCreateForm.libelle}
                    onChange={(e) => setTimerCreateForm((p) => ({ ...p, libelle: e.target.value }))}
                    required
                  />
                </div>

                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
                  <div>
                    <FieldLabel htmlFor="t-minutes">Valeur (minutes)</FieldLabel>
                    <StyledInput
                      id="t-minutes"
                      type="number"
                      min={0}
                      value={timerCreateForm.valeurMinutes}
                      onChange={(e) =>
                        setTimerCreateForm((p) => ({ ...p, valeurMinutes: toNumber(e.target.value) }))
                      }
                      required
                    />
                  </div>
                  <div>
                    <FieldLabel>Statut</FieldLabel>
                    <ToggleSwitch
                      checked={timerCreateForm.actif}
                      onChange={() => setTimerCreateForm((p) => ({ ...p, actif: !p.actif }))}
                      label={timerCreateForm.actif ? 'Actif' : 'Inactif'}
                    />
                  </div>
                </div>

                <div
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    paddingTop: 12,
                    borderTop: '1px solid #f1f5f9',
                    marginTop: 4,
                  }}
                >
                  <p style={{ fontSize: '0.75rem', color: '#94a3b8' }}>
                    Tous les champs sont obligatoires
                  </p>
                  <PrimaryBtn
                    loading={pendingAction === 'create-timer'}
                    label="Enregistrer"
                    loadingLabel="Envoi..."
                  />
                </div>
              </form>
            </SectionCard>

            {/* ── Liste des timers ── */}
            <SectionCard
              icon={<List style={{ width: 18, height: 18 }} />}
              title="Liste des timers"
              subtitle={`${timersList.length} timer(s) enregistré(s)`}
              index={2}
            >
              <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
                {/* Bouton de rechargement */}
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                  <p style={{ fontSize: '0.75rem', color: '#94a3b8' }}>
                    Cliquez sur "Charger" pour afficher la liste
                  </p>
                  <motion.button
                    type="button"
                    onClick={loadTimers}
                    disabled={loadingTimers}
                    whileHover={{ scale: loadingTimers ? 1 : 1.02 }}
                    whileTap={{ scale: 0.97 }}
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: 8,
                      padding: '0 16px',
                      height: 38,
                      background: loadingTimers ? '#e2e8f0' : '#f1f5f9',
                      border: '1.5px solid #cbd5e1',
                      borderRadius: 10,
                      color: '#475569',
                      fontWeight: 600,
                      fontSize: '0.8rem',
                      cursor: loadingTimers ? 'not-allowed' : 'pointer',
                    }}
                  >
                    {loadingTimers ? (
                      <>
                        <Loader2 style={{ width: 14, height: 14, animation: 'spin .75s linear infinite' }} />
                        Chargement...
                      </>
                    ) : (
                      <>
                        <RefreshCw style={{ width: 14, height: 14 }} />
                        Charger
                      </>
                    )}
                  </motion.button>
                </div>

                {/* Liste */}
                {loadingTimers ? (
                  <div style={{
                    padding: '40px 20px',
                    textAlign: 'center',
                    color: '#94a3b8',
                    fontSize: '0.875rem'
                  }}>
                    <Loader2 style={{ width: 24, height: 24, margin: '0 auto 12px', animation: 'spin .75s linear infinite' }} />
                    Chargement des timers...
                  </div>
                ) : !timersLoaded ? null : timersList.length === 0 ? (
                  <div style={{
                    padding: '40px 20px',
                    textAlign: 'center',
                    color: '#94a3b8',
                    fontSize: '0.875rem',
                    fontStyle: 'italic'
                  }}>
                    Aucun timer trouvé.
                  </div>
                ) : (
                  <div style={{ display: 'flex', flexDirection: 'column', gap: 8, maxHeight: 400, overflowY: 'auto' }}>
                    {timersList.map((timer) => {
                      const isEditing = editingTimerId === timer.id;

                      return (
                        <motion.div
                          key={timer.id}
                          initial={{ opacity: 0, y: 10 }}
                          animate={{ opacity: 1, y: 0 }}
                          style={{
                            background: isEditing ? '#eff6ff' : '#f8fafc',
                            border: `1.5px solid ${isEditing ? '#3b82f6' : '#e2e8f0'}`,
                            borderRadius: 12,
                            padding: 14,
                          }}
                        >
                          {isEditing && editingTimerData ? (
                            /* Mode édition */
                            <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
                              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8 }}>
                                <input
                                  type="text"
                                  value={editingTimerData.code}
                                  onChange={(e) => updateEditingTimerField('code', e.target.value)}
                                  placeholder="Code"
                                  style={{
                                    padding: '8px 12px',
                                    fontSize: '0.85rem',
                                    fontFamily: 'monospace',
                                    fontWeight: 700,
                                    border: '1.5px solid #cbd5e1',
                                    borderRadius: 8,
                                    outline: 'none',
                                  }}
                                />
                                <input
                                  type="number"
                                  value={editingTimerData.valeurMinutes}
                                  onChange={(e) => updateEditingTimerField('valeurMinutes', parseInt(e.target.value) || 0)}
                                  placeholder="Minutes"
                                  style={{
                                    padding: '8px 12px',
                                    fontSize: '0.85rem',
                                    fontWeight: 600,
                                    border: '1.5px solid #cbd5e1',
                                    borderRadius: 8,
                                    outline: 'none',
                                  }}
                                />
                              </div>
                              <input
                                type="text"
                                value={editingTimerData.libelle}
                                onChange={(e) => updateEditingTimerField('libelle', e.target.value)}
                                placeholder="Libellé"
                                style={{
                                  padding: '8px 12px',
                                  fontSize: '0.875rem',
                                  fontWeight: 600,
                                  border: '1.5px solid #cbd5e1',
                                  borderRadius: 8,
                                  outline: 'none',
                                }}
                              />
                              <label style={{ display: 'flex', alignItems: 'center', gap: 8, cursor: 'pointer' }}>
                                <input
                                  type="checkbox"
                                  checked={editingTimerData.actif}
                                  onChange={(e) => updateEditingTimerField('actif', e.target.checked)}
                                  style={{ width: 16, height: 16, cursor: 'pointer' }}
                                />
                                <span style={{ fontSize: '0.875rem', fontWeight: 600, color: '#475569' }}>
                                  Timer actif
                                </span>
                              </label>
                              <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
                                <motion.button
                                  type="button"
                                  onClick={cancelEditingTimer}
                                  whileHover={{ scale: 1.05 }}
                                  whileTap={{ scale: 0.95 }}
                                  style={{
                                    display: 'flex',
                                    alignItems: 'center',
                                    gap: 6,
                                    padding: '8px 14px',
                                    background: 'white',
                                    border: '1.5px solid #e2e8f0',
                                    borderRadius: 8,
                                    fontSize: '0.8rem',
                                    fontWeight: 600,
                                    color: '#64748b',
                                    cursor: 'pointer',
                                  }}
                                >
                                  <X style={{ width: 14, height: 14 }} />
                                  Annuler
                                </motion.button>
                                <motion.button
                                  type="button"
                                  onClick={handlePatchTimer}
                                  disabled={pendingAction === `edit-timer-${timer.id}`}
                                  whileHover={{ scale: 1.05 }}
                                  whileTap={{ scale: 0.95 }}
                                  style={{
                                    display: 'flex',
                                    alignItems: 'center',
                                    gap: 6,
                                    padding: '8px 14px',
                                    background: GREEN_GRAD,
                                    border: 'none',
                                    borderRadius: 8,
                                    fontSize: '0.8rem',
                                    fontWeight: 600,
                                    color: 'white',
                                    cursor: pendingAction === `edit-timer-${timer.id}` ? 'not-allowed' : 'pointer',
                                  }}
                                >
                                  {pendingAction === `edit-timer-${timer.id}` ? (
                                    <>
                                      <Loader2 style={{ width: 14, height: 14, animation: 'spin .75s linear infinite' }} />
                                      Enregistrement...
                                    </>
                                  ) : (
                                    <>
                                      <Save style={{ width: 14, height: 14 }} />
                                      Enregistrer
                                    </>
                                  )}
                                </motion.button>
                              </div>
                            </div>
                          ) : (
                            /* Mode affichage */
                            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 12 }}>
                              <div style={{ flex: 1 }}>
                                <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 6 }}>
                                  <span style={{
                                    fontSize: '0.85rem',
                                    fontWeight: 700,
                                    color: '#1e293b',
                                    fontFamily: 'monospace',
                                    background: '#dbeafe',
                                    padding: '2px 8px',
                                    borderRadius: 6,
                                  }}>
                                    {timer.code}
                                  </span>
                                  <span style={{
                                    fontSize: '0.75rem',
                                    fontWeight: 600,
                                    color: '#f59e0b',
                                    background: '#fef3c7',
                                    padding: '2px 8px',
                                    borderRadius: 6,
                                  }}>
                                    {timer.valeurMinutes} min
                                  </span>
                                  <span style={{
                                    fontSize: '0.7rem',
                                    fontWeight: 600,
                                    color: timer.actif ? '#16a34a' : '#94a3b8',
                                    background: timer.actif ? '#dcfce7' : '#f1f5f9',
                                    padding: '2px 8px',
                                    borderRadius: 6,
                                    textTransform: 'uppercase',
                                  }}>
                                    {timer.actif ? 'ACTIF' : 'INACTIF'}
                                  </span>
                                </div>
                                <p style={{ fontSize: '0.875rem', fontWeight: 600, color: '#475569' }}>
                                  {timer.libelle}
                                </p>
                              </div>

                              <div style={{ display: 'flex', gap: 6 }}>
                                <motion.button
                                  type="button"
                                  onClick={() => startEditingTimer(timer)}
                                  whileHover={{ scale: 1.1 }}
                                  whileTap={{ scale: 0.9 }}
                                  style={{
                                    width: 32,
                                    height: 32,
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                    background: '#dbeafe',
                                    border: '1.5px solid #bfdbfe',
                                    borderRadius: 8,
                                    color: '#1d4ed8',
                                    cursor: 'pointer',
                                    flexShrink: 0,
                                  }}
                                  title="Modifier"
                                >
                                  <Edit3 style={{ width: 14, height: 14 }} />
                                </motion.button>

                                <motion.button
                                  type="button"
                                  onClick={() => openDeleteTimerDialog(timer)}
                                  disabled={!timer.id}
                                  whileHover={{ scale: 1.1 }}
                                  whileTap={{ scale: 0.9 }}
                                  style={{
                                    width: 32,
                                    height: 32,
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                    background: '#fee2e2',
                                    border: '1.5px solid #fecaca',
                                    borderRadius: 8,
                                    color: '#dc2626',
                                    cursor: !timer.id ? 'not-allowed' : 'pointer',
                                    flexShrink: 0,
                                  }}
                                  title="Supprimer"
                                >
                                  <Trash2 style={{ width: 14, height: 14 }} />
                                </motion.button>
                              </div>
                            </div>
                          )}
                        </motion.div>
                      );
                    })}
                  </div>
                )}
              </div>
            </SectionCard>
          </motion.div>
        ) : (
          /* ── CODE OPERATIONS ── */
          <motion.div
            key="code-operations"
            custom={direction}
            variants={panelVariants}
            initial="hidden"
            animate="visible"
            exit="exit"
            style={{
              display: 'grid',
              gridTemplateColumns: 'repeat(auto-fit, minmax(340px, 1fr))',
              gap: 20,
            }}
          >
            {/* ── Créer un code opération ── */}
            <SectionCard
              icon={<Plus style={{ width: 18, height: 18 }} />}
              title="Créer un code opération"
              subtitle="L'identifiant est auto-incrémenté côté backend."
              index={0}
            >
              <form onSubmit={handleCreateCodeOperation} style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
                <div>
                  <FieldLabel htmlFor="co-code">Code opération</FieldLabel>
                  <StyledInput
                    id="co-code"
                    placeholder="Ex : RETRAIT_GUICHET"
                    value={codeOpCreateForm.codeOpe}
                    onChange={(e) =>
                      setCodeOpCreateForm((p) => ({ ...p, codeOpe: e.target.value }))
                    }
                    required
                  />
                </div>

                <div>
                  <FieldLabel htmlFor="co-libelle">Libellé</FieldLabel>
                  <StyledInput
                    id="co-libelle"
                    placeholder="Ex : Retrait guichet"
                    value={codeOpCreateForm.libelle}
                    onChange={(e) =>
                      setCodeOpCreateForm((p) => ({ ...p, libelle: e.target.value }))
                    }
                    required
                  />
                </div>

                <div>
                  <FieldLabel htmlFor="co-desc">Description</FieldLabel>
                  <StyledTextarea
                    id="co-desc"
                    placeholder="Description détaillée de l'opération..."
                    value={codeOpCreateForm.description}
                    onChange={(e) =>
                      setCodeOpCreateForm((p) => ({ ...p, description: e.target.value }))
                    }
                    required
                  />
                </div>

                <div>
                  <FieldLabel htmlFor="co-statut">Statut</FieldLabel>
                  <StyledInput
                    id="co-statut"
                    placeholder="Ex : ACTIF"
                    value={codeOpCreateForm.statut}
                    onChange={(e) =>
                      setCodeOpCreateForm((p) => ({ ...p, statut: e.target.value }))
                    }
                    required
                  />
                </div>

                <div
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    paddingTop: 12,
                    borderTop: '1px solid #f1f5f9',
                    marginTop: 4,
                  }}
                >
                  <p style={{ fontSize: '0.75rem', color: '#94a3b8' }}>
                    Tous les champs sont obligatoires
                  </p>
                  <PrimaryBtn
                    loading={pendingAction === 'create-code-operation'}
                    label="Enregistrer"
                    loadingLabel="Envoi..."
                  />
                </div>
              </form>
            </SectionCard>

            {/* ── Liste des codes opérations ── */}
            <SectionCard
              icon={<List style={{ width: 18, height: 18 }} />}
              title="Liste des codes opérations"
              subtitle={`${codeOperationsList.length} code(s) enregistré(s)`}
              index={1}
            >
              <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
                {/* Bouton de rechargement */}
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                  <p style={{ fontSize: '0.75rem', color: '#94a3b8' }}>
                    Cliquez sur "Charger" pour afficher la liste
                  </p>
                  <motion.button
                    type="button"
                    onClick={loadCodeOperations}
                    disabled={loadingCodeOps}
                    whileHover={{ scale: loadingCodeOps ? 1 : 1.02 }}
                    whileTap={{ scale: 0.97 }}
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: 8,
                      padding: '0 16px',
                      height: 38,
                      background: loadingCodeOps ? '#e2e8f0' : '#f1f5f9',
                      border: '1.5px solid #cbd5e1',
                      borderRadius: 10,
                      color: '#475569',
                      fontWeight: 600,
                      fontSize: '0.8rem',
                      cursor: loadingCodeOps ? 'not-allowed' : 'pointer',
                    }}
                  >
                    {loadingCodeOps ? (
                      <>
                        <Loader2 style={{ width: 14, height: 14, animation: 'spin .75s linear infinite' }} />
                        Chargement...
                      </>
                    ) : (
                      <>
                        <RefreshCw style={{ width: 14, height: 14 }} />
                        Charger
                      </>
                    )}
                  </motion.button>
                </div>

                {/* Liste */}
                {loadingCodeOps ? (
                  <div style={{
                    padding: '40px 20px',
                    textAlign: 'center',
                    color: '#94a3b8',
                    fontSize: '0.875rem'
                  }}>
                    <Loader2 style={{ width: 24, height: 24, margin: '0 auto 12px', animation: 'spin .75s linear infinite' }} />
                    Chargement des codes opérations...
                  </div>
                ) : !codeOpsLoaded ? null : codeOperationsList.length === 0 ? (
                  <div style={{
                    padding: '40px 20px',
                    textAlign: 'center',
                    color: '#94a3b8',
                    fontSize: '0.875rem',
                    fontStyle: 'italic'
                  }}>
                    Aucun code opération trouvé.
                  </div>
                ) : (
                  <div style={{ display: 'flex', flexDirection: 'column', gap: 8, maxHeight: 400, overflowY: 'auto' }}>
                    {codeOperationsList.map((codeOp) => {
                      const isEditing = editingCodeOpId === codeOp.id;

                      return (
                        <motion.div
                          key={codeOp.id}
                          initial={{ opacity: 0, y: 10 }}
                          animate={{ opacity: 1, y: 0 }}
                          style={{
                            background: isEditing ? '#eff6ff' : '#f8fafc',
                            border: `1.5px solid ${isEditing ? '#3b82f6' : '#e2e8f0'}`,
                            borderRadius: 12,
                            padding: 14,
                          }}
                        >
                          {isEditing && editingCodeOpData ? (
                            /* Mode édition */
                            <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
                              <div style={{ display: 'grid', gridTemplateColumns: '1fr 120px', gap: 8 }}>
                                <input
                                  type="text"
                                  value={editingCodeOpData.codeOpe}
                                  onChange={(e) => updateEditingField('codeOpe', e.target.value)}
                                  placeholder="Code opération"
                                  style={{
                                    padding: '8px 12px',
                                    fontSize: '0.85rem',
                                    fontFamily: 'monospace',
                                    fontWeight: 700,
                                    border: '1.5px solid #cbd5e1',
                                    borderRadius: 8,
                                    outline: 'none',
                                  }}
                                />
                                <input
                                  type="text"
                                  value={editingCodeOpData.statut}
                                  onChange={(e) => updateEditingField('statut', e.target.value)}
                                  placeholder="Statut"
                                  style={{
                                    padding: '8px 12px',
                                    fontSize: '0.75rem',
                                    fontWeight: 600,
                                    border: '1.5px solid #cbd5e1',
                                    borderRadius: 8,
                                    outline: 'none',
                                    textTransform: 'uppercase',
                                  }}
                                />
                              </div>
                              <input
                                type="text"
                                value={editingCodeOpData.libelle}
                                onChange={(e) => updateEditingField('libelle', e.target.value)}
                                placeholder="Libellé"
                                style={{
                                  padding: '8px 12px',
                                  fontSize: '0.875rem',
                                  fontWeight: 600,
                                  border: '1.5px solid #cbd5e1',
                                  borderRadius: 8,
                                  outline: 'none',
                                }}
                              />
                              <textarea
                                value={editingCodeOpData.description}
                                onChange={(e) => updateEditingField('description', e.target.value)}
                                placeholder="Description"
                                rows={2}
                                style={{
                                  padding: '8px 12px',
                                  fontSize: '0.75rem',
                                  border: '1.5px solid #cbd5e1',
                                  borderRadius: 8,
                                  outline: 'none',
                                  resize: 'none',
                                  fontFamily: 'inherit',
                                }}
                              />
                              <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
                                <motion.button
                                  type="button"
                                  onClick={cancelEditingCodeOp}
                                  whileHover={{ scale: 1.05 }}
                                  whileTap={{ scale: 0.95 }}
                                  style={{
                                    display: 'flex',
                                    alignItems: 'center',
                                    gap: 6,
                                    padding: '8px 14px',
                                    background: 'white',
                                    border: '1.5px solid #e2e8f0',
                                    borderRadius: 8,
                                    fontSize: '0.8rem',
                                    fontWeight: 600,
                                    color: '#64748b',
                                    cursor: 'pointer',
                                  }}
                                >
                                  <X style={{ width: 14, height: 14 }} />
                                  Annuler
                                </motion.button>
                                <motion.button
                                  type="button"
                                  onClick={handlePatchCodeOperation}
                                  disabled={pendingAction === `edit-${codeOp.id}`}
                                  whileHover={{ scale: 1.05 }}
                                  whileTap={{ scale: 0.95 }}
                                  style={{
                                    display: 'flex',
                                    alignItems: 'center',
                                    gap: 6,
                                    padding: '8px 14px',
                                    background: GREEN_GRAD,
                                    border: 'none',
                                    borderRadius: 8,
                                    fontSize: '0.8rem',
                                    fontWeight: 600,
                                    color: 'white',
                                    cursor: pendingAction === `edit-${codeOp.id}` ? 'not-allowed' : 'pointer',
                                  }}
                                >
                                  {pendingAction === `edit-${codeOp.id}` ? (
                                    <>
                                      <Loader2 style={{ width: 14, height: 14, animation: 'spin .75s linear infinite' }} />
                                      Enregistrement...
                                    </>
                                  ) : (
                                    <>
                                      <Save style={{ width: 14, height: 14 }} />
                                      Enregistrer
                                    </>
                                  )}
                                </motion.button>
                              </div>
                            </div>
                          ) : (
                            /* Mode affichage */
                            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 12 }}>
                              <div style={{ flex: 1 }}>
                                <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 6 }}>
                                  <span style={{
                                    fontSize: '0.85rem',
                                    fontWeight: 700,
                                    color: '#1e293b',
                                    fontFamily: 'monospace',
                                    background: '#dbeafe',
                                    padding: '2px 8px',
                                    borderRadius: 6,
                                  }}>
                                    {codeOp.codeOpe}
                                  </span>
                                  <span style={{
                                    fontSize: '0.7rem',
                                    fontWeight: 600,
                                    color: codeOp.statut === 'ACTIF' ? '#16a34a' : '#94a3b8',
                                    background: codeOp.statut === 'ACTIF' ? '#dcfce7' : '#f1f5f9',
                                    padding: '2px 8px',
                                    borderRadius: 6,
                                    textTransform: 'uppercase',
                                  }}>
                                    {codeOp.statut}
                                  </span>
                                </div>
                                <p style={{ fontSize: '0.875rem', fontWeight: 600, color: '#475569', marginBottom: 4 }}>
                                  {codeOp.libelle}
                                </p>
                                <p style={{ fontSize: '0.75rem', color: '#94a3b8', lineHeight: 1.5 }}>
                                  {codeOp.description}
                                </p>
                              </div>

                              <div style={{ display: 'flex', gap: 6 }}>
                                <motion.button
                                  type="button"
                                  onClick={() => startEditingCodeOp(codeOp)}
                                  whileHover={{ scale: 1.1 }}
                                  whileTap={{ scale: 0.9 }}
                                  style={{
                                    width: 32,
                                    height: 32,
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                    background: '#dbeafe',
                                    border: '1.5px solid #bfdbfe',
                                    borderRadius: 8,
                                    color: '#1d4ed8',
                                    cursor: 'pointer',
                                    flexShrink: 0,
                                  }}
                                  title="Modifier"
                                >
                                  <Edit3 style={{ width: 14, height: 14 }} />
                                </motion.button>

                                <motion.button
                                  type="button"
                                  onClick={() => openDeleteCodeOperationDialog(codeOp)}
                                  disabled={!codeOp.id}
                                  whileHover={{ scale: 1.1 }}
                                  whileTap={{ scale: 0.9 }}
                                  style={{
                                    width: 32,
                                    height: 32,
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                    background: '#fee2e2',
                                    border: '1.5px solid #fecaca',
                                    borderRadius: 8,
                                    color: '#dc2626',
                                    cursor: !codeOp.id ? 'not-allowed' : 'pointer',
                                    flexShrink: 0,
                                  }}
                                  title="Supprimer"
                                >
                                  <Trash2 style={{ width: 14, height: 14 }} />
                                </motion.button>
                              </div>
                            </div>
                          )}
                        </motion.div>
                      );
                    })}
                  </div>
                )}
              </div>
            </SectionCard>
          </motion.div>
        )}
      </AnimatePresence>

      {/* ── Delete Confirmation Dialog ─────────────────────── */}
      <AnimatePresence>
        {deleteDialog.isOpen && (
          <>
            {/* Backdrop */}
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              transition={{ duration: 0.2 }}
              onClick={closeDeleteDialog}
              style={{
                position: 'fixed',
                top: 0,
                left: 0,
                right: 0,
                bottom: 0,
                background: 'rgba(0, 0, 0, 0.5)',
                zIndex: 9998,
                backdropFilter: 'blur(4px)',
              }}
            />

            {/* Dialog */}
            <motion.div
              initial={{ opacity: 0, scale: 0.9, y: 20 }}
              animate={{ opacity: 1, scale: 1, y: 0 }}
              exit={{ opacity: 0, scale: 0.9, y: 20 }}
              transition={{ type: 'spring', duration: 0.3 }}
              style={{
                position: 'fixed',
                top: '50%',
                left: '50%',
                transform: 'translate(-50%, -50%)',
                zIndex: 9999,
                width: '90%',
                maxWidth: 480,
              }}
            >
              <div
                style={{
                  background: 'white',
                  borderRadius: 20,
                  boxShadow: '0 20px 60px rgba(0, 0, 0, 0.3)',
                  overflow: 'hidden',
                }}
              >
                {/* Header */}
                <div
                  style={{
                    padding: '24px 28px',
                    background: 'linear-gradient(135deg, #dc2626 0%, #991b1b 100%)',
                    display: 'flex',
                    alignItems: 'center',
                    gap: 16,
                  }}
                >
                  <div
                    style={{
                      width: 48,
                      height: 48,
                      borderRadius: 12,
                      background: 'rgba(255, 255, 255, 0.2)',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                    }}
                  >
                    <AlertTriangle style={{ width: 24, height: 24, color: 'white' }} />
                  </div>
                  <div>
                    <h3 style={{ fontSize: '1.25rem', fontWeight: 700, color: 'white', margin: 0 }}>
                      Confirmer la suppression
                    </h3>
                    <p style={{ fontSize: '0.875rem', color: 'rgba(255, 255, 255, 0.9)', margin: '4px 0 0' }}>
                      Cette action est irréversible
                    </p>
                  </div>
                </div>

                {/* Body */}
                <div style={{ padding: '24px 28px' }}>
                  <p style={{ fontSize: '0.9375rem', color: '#475569', lineHeight: 1.6, margin: 0 }}>
                    Êtes-vous sûr de vouloir supprimer{' '}
                    <strong style={{ color: '#1e293b' }}>
                      {deleteDialog.type === 'timer' ? 'le timer' : 'le code opération'}
                    </strong>{' '}
                    "<strong style={{ color: '#dc2626' }}>{deleteDialog.name}</strong>" ?
                  </p>
                </div>

                {/* Footer */}
                <div
                  style={{
                    padding: '20px 28px',
                    background: '#f8fafc',
                    display: 'flex',
                    gap: 12,
                    justifyContent: 'flex-end',
                  }}
                >
                  <motion.button
                    type="button"
                    onClick={closeDeleteDialog}
                    disabled={pendingAction !== null}
                    whileHover={{ scale: 1.02 }}
                    whileTap={{ scale: 0.98 }}
                    style={{
                      padding: '10px 20px',
                      background: 'white',
                      border: '1.5px solid #e2e8f0',
                      borderRadius: 10,
                      fontSize: '0.875rem',
                      fontWeight: 600,
                      color: '#64748b',
                      cursor: pendingAction !== null ? 'not-allowed' : 'pointer',
                      opacity: pendingAction !== null ? 0.6 : 1,
                    }}
                  >
                    Annuler
                  </motion.button>
                  <motion.button
                    type="button"
                    onClick={deleteDialog.type === 'timer' ? handleDeleteTimer : handleDeleteCodeOperation}
                    disabled={pendingAction !== null}
                    whileHover={{ scale: 1.02 }}
                    whileTap={{ scale: 0.98 }}
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: 8,
                      padding: '10px 20px',
                      background: pendingAction !== null ? '#fca5a5' : 'linear-gradient(135deg, #dc2626 0%, #991b1b 100%)',
                      border: 'none',
                      borderRadius: 10,
                      fontSize: '0.875rem',
                      fontWeight: 600,
                      color: 'white',
                      cursor: pendingAction !== null ? 'not-allowed' : 'pointer',
                      boxShadow: pendingAction !== null ? 'none' : '0 4px 14px rgba(220, 38, 38, 0.35)',
                    }}
                  >
                    {pendingAction !== null ? (
                      <>
                        <Loader2 style={{ width: 16, height: 16, animation: 'spin .75s linear infinite' }} />
                        Suppression...
                      </>
                    ) : (
                      <>
                        <Trash2 style={{ width: 16, height: 16 }} />
                        Supprimer
                      </>
                    )}
                  </motion.button>
                </div>
              </div>
            </motion.div>
          </>
        )}
      </AnimatePresence>

      {/* Spinner keyframe */}
      <style>{`
        @keyframes spin { to { transform: rotate(360deg); } }
      `}</style>
    </div>
  );
}
